import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Box, InputBase, styled, TextareaAutosize } from "@mui/material";
import { JSX } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { v4 } from "uuid";
import { CHECKLIST_QUESTION_TYPE } from "../../constants";
import { IChecklistQuestionType } from "../../interfaces/CheckLists";
import { useLocale } from "../../providers/LocaleProvider";
import { Button } from "../Button";
import { DatePicker } from "../DatePicker";
import OptionItem from "./OptionItem";

const editorStyles = {
  ml: 6,
  mt: 1,
  bgcolor: "grey.50",
  border: "1px solid var(--grey-gr-200)",
  borderRadius: 2
};
const StyledTextarea = styled(TextareaAutosize)(({ theme }) => ({
  "&::placeholder": {
    color: "grey.900",
    fontFamily: "Inter",
    fontSize: "16px",
    opacity: 0.6,
    backgroundColor: "#fafafa"
  }
}));

const getQuestionTypePreviews = (translate: (key: string) => string): Map<string, JSX.Element> =>
  new Map([
    [
      CHECKLIST_QUESTION_TYPE.SHORT_ANSWER,
      <Box sx={editorStyles} key={CHECKLIST_QUESTION_TYPE.SHORT_ANSWER}>
        <InputBase
          className='klayo-textfield'
          disabled
          placeholder={translate("settingsViews.checklists.shortTextResponse")}
          sx={{ width: "60%" }}
        />
      </Box>
    ],
    [
      CHECKLIST_QUESTION_TYPE.LONG_ANSWER,
      <Box sx={editorStyles} key={CHECKLIST_QUESTION_TYPE.SHORT_ANSWER}>
        <StyledTextarea
          key={CHECKLIST_QUESTION_TYPE.LONG_ANSWER}
          className='klayo-textfield'
          disabled
          placeholder={translate("settingsViews.checklists.longTextResponse")}
          style={{
            width: "90%",
            resize: "none",
            border: "none"
          }}
          minRows={3}
          maxRows={10}
        />
      </Box>
    ],
    [
      CHECKLIST_QUESTION_TYPE.DATE,
      <Box sx={{ marginLeft: 5, marginTop: 1 }} key={CHECKLIST_QUESTION_TYPE.DATE}>
        <DatePicker disabled />
      </Box>
    ]
  ]);

interface QuestionEditorProps {
  listQuestionTypes?: IChecklistQuestionType[];
  name: string;
  triggerValidationValueDebounced?: () => void;
}

export default function QuestionEditor({
  listQuestionTypes,
  name,
  triggerValidationValueDebounced
}: QuestionEditorProps) {
  const { translate } = useLocale();
  const { watch, control, setFocus } = useFormContext();
  const options = watch(`${name}.options`);
  const typeId = watch(`${name}.checklistQuestionTypeId`);
  const questionType = listQuestionTypes?.find(
    (i: IChecklistQuestionType) => i.id === typeId
  )?.code;

  const {
    fields: optionFields,
    append: appendOption,
    replace: replaceOption
  } = useFieldArray({
    control,
    name: `${name}.options`
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  const handleAddOption = () => {
    appendOption({
      id: v4(),
      optionText: ``,
      displayOrder: options?.length
    });
    triggerValidationValueDebounced?.();
    setFocus(`${name}.options.${options?.length}.optionText`);
  };

  const handleOptionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = options?.findIndex((item: any) => item.id === active.id);
      const newIndex = options?.findIndex((item: any) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedOptions = arrayMove(options, oldIndex, newIndex);

        const finalOptions = reorderedOptions.map((option: any, index: number) => ({
          ...option,
          displayOrder: index
        }));
        replaceOption(finalOptions);
      }
    }
    triggerValidationValueDebounced?.();
  };

  const handleRemoveOptionWithReindex = (removeIndex: number) => {
    const remaining = (options || [])
      .filter((_: any, idx: number) => idx !== removeIndex)
      .map((o: any, idx: number) => ({ ...o, displayOrder: idx }));
    replaceOption(remaining);
    triggerValidationValueDebounced?.();
  };

  const disableAddOption = optionFields?.length >= 15;

  const questionTypePreviews = getQuestionTypePreviews(translate);
  const matchedPreview = questionType && questionTypePreviews.get(questionType);
  if (matchedPreview) {
    return matchedPreview;
  }

  return (
    <>
      <DndContext sensors={sensors} onDragEnd={handleOptionDragEnd}>
        <SortableContext
          items={options?.map((item: any) => item.id) || []}
          strategy={verticalListSortingStrategy}
        >
          <Box sx={{ display: "flex", ml: 2, flexDirection: "column", mt: 1, gap: 1 }}>
            {options?.map((option: any, optIndex: number) => (
              <OptionItem
                key={option.id}
                optIndex={optIndex}
                questionType={questionType}
                name={name}
                option={option}
                onRemoveOption={handleRemoveOptionWithReindex}
                canRemove={options?.length > 1}
                triggerValidationValueDebounced={triggerValidationValueDebounced}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
      <Button
        sx={{ ml: 7, mt: 1, fontSize: "16px" }}
        size='md'
        variant='text'
        label={translate("common.addOption")}
        type='button'
        onClick={handleAddOption}
        disabled={disableAddOption}
      />
    </>
  );
}
